import type { ProfileIssue, SyncBondsOrdersRequest } from '~/services/generated/client';

export function useSyncBondsOrders() {
  const userState = useUserState();

  const { investorProfileApi } = useApi();

  const { handleAsyncOperation } = useAsyncOperationHandler();

  // state
  const isSyncBondsOrdersEnabled = useState('isSyncBondsOrdersEnabled', () => false);

  const unsynchronizedIssues = useState('unsynchronizedIssues', () => new Set<ProfileIssue>());

  // loadings
  const isUnsynchronizedIssuesFetching = ref(false);

  const isSyncBondsOrdersEnabledLoading = useState('isSyncBondsOrdersEnabledLoading', () => false);

  // methods
  const getSyncBondsOrdersState = () => handleAsyncOperation(
    async () => {
      const investorProfile = await investorProfileApi.value.getInvestorProfile({ xCFGINVESTORUSERID: userState.value?.sub || '' });

      isSyncBondsOrdersEnabled.value = investorProfile.syncBondsOrders;
    },
    { errorTitleKey: 'settings.syncBondsOrders.api.get.error.title' }
  );

  const setSyncBondsOrdersState = (params: Omit<SyncBondsOrdersRequest, 'xCFGINVESTORUSERID'>) => handleAsyncOperation(
    async () => {
      isSyncBondsOrdersEnabledLoading.value = true;

      isSyncBondsOrdersEnabled.value = params.profileSync?.syncBondsOrders ?? false;

      await investorProfileApi.value.syncBondsOrders({ ...params, xCFGINVESTORUSERID: userState.value?.sub || '' });

      isSyncBondsOrdersEnabledLoading.value = false;
    },
    {
      successTitleKey: `settings.syncBondsOrders.api.set.success.${params.profileSync?.syncBondsOrders}.title`,
      errorTitleKey: 'settings.syncBondsOrders.api.set.error.title'
    }
  );

  const getUnsynchronizedIssues = () => handleAsyncOperation(
    async () => {
      isUnsynchronizedIssuesFetching.value = true;

      const response = await investorProfileApi.value.listInvestorProfileBondsIssues({ xCFGINVESTORUSERID: userState.value?.sub || '' });

      unsynchronizedIssues.value = new Set(response);
      isUnsynchronizedIssuesFetching.value = false;
    },
    { errorTitleKey: 'settings.syncBondsOrders.api.getUnsynchronizedIssues.error.title' }
  );

  return {
    getSyncBondsOrdersState,
    setSyncBondsOrdersState,
    isSyncBondsOrdersEnabled,
    isSyncBondsOrdersEnabledLoading,

    getUnsynchronizedIssues,
    isUnsynchronizedIssuesFetching,
    unsynchronizedIssues: computed({
      get() {
        return unsynchronizedIssues.value;
      },
      set(value: Set<ProfileIssue>) {
        unsynchronizedIssues.value = value;
      }
    }),
  };
}
